import type React from 'react';

import type { EventId } from '@vlabs/api/luna-api/model/EventId';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { isUUID } from '@vlabs/shared/validators';
import { Tag } from '@vlabs/uikit';

import { useEventQuery, useStreamQuery } from '@vlabs/monokit/entities/events/api';

import st from './EventDetailsGeneralInfo.module.sass';
import { useEventDetailsGeneralInfoTranslation } from './i18n';
import {
  DeepfakeRow,
  LivenessRow,
  EventIdRow,
  TrackIdRow,
  HandlerRow,
  TagsRow,
  MatchByListOrParameter,
} from './rows';
import { BaseGeneralEventInfo, GeneralEventInfoRow } from '../../general-event/@x/event-details';

interface EventDetailsGeneralInfoProps {
  eventId: EventId;
  canReadHandler?: boolean;
  canViewHandlersPage?: boolean;
}

function EventDetailsGeneralInfo({
  eventId,
  canReadHandler = true,
  canViewHandlersPage = true,
}: EventDetailsGeneralInfoProps): React.JSX.Element {
  const { t } = useEventDetailsGeneralInfoTranslation();
  const { data: event } = useEventQuery(eventId);
  const { data: stream } = useStreamQuery(event?.stream_id);
  const topMatchLabel = event?.top_match?.label as string | undefined;
  let topMatchTitle = t('match_by_param');

  if (topMatchLabel && isUUID(topMatchLabel)) {
    topMatchTitle = t('match_by_list');
  }

  return (
    <BaseGeneralEventInfo>
      <h6 className={st.Title}>{t('title')}</h6>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('event_type')}
      >
        <Tag
          kind="neutral"
          size="large"
          value={event?.top_match ? t('match') : t('detection')}
        />
      </GeneralEventInfoRow>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('create_time')}
      >
        {parseISOToLocale(event?.create_time)}
      </GeneralEventInfoRow>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('event_id')}
      >
        {event?.event_id ? <EventIdRow id={event.event_id} /> : undefined}
      </GeneralEventInfoRow>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('track_id')}
      >
        {event?.track_id ? <TrackIdRow id={event.track_id} /> : undefined}
      </GeneralEventInfoRow>

      {!!canReadHandler && (
        <GeneralEventInfoRow
          className={st.InfoRow}
          title={t('handlers')}
        >
          {event?.handler_id ? (
            <HandlerRow
              id={event.handler_id}
              linkEnabled={canViewHandlersPage}
            />
          ) : undefined}
        </GeneralEventInfoRow>
      )}

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('source')}
      >
        {event?.source}
      </GeneralEventInfoRow>

      {event?.source !== stream?.name
        && (
          <GeneralEventInfoRow
            className={st.InfoRow}
            title={t('stream_id')}
          >
            {stream?.name}
          </GeneralEventInfoRow>
        )}

      {!!topMatchLabel && (
        <GeneralEventInfoRow
          className={st.InfoRow}
          title={topMatchTitle}
        >
          <MatchByListOrParameter label={topMatchLabel} />
        </GeneralEventInfoRow>
      )}

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('tags')}
      >
        {Array.isArray(event?.tags) ? <TagsRow tags={event.tags} /> : undefined}
      </GeneralEventInfoRow>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('liveness')}
      >
        <LivenessRow liveness={event?.liveness as never} />
      </GeneralEventInfoRow>

      <GeneralEventInfoRow
        className={st.InfoRow}
        title={t('deepfake')}
      >
        <DeepfakeRow deepfake={event?.deepfake as never} />
      </GeneralEventInfoRow>
    </BaseGeneralEventInfo>
  );
}

const displayName = 'EventDetailsGeneralInfo';

EventDetailsGeneralInfo.displayName = displayName;

export { EventDetailsGeneralInfo };
